/**
 * Importación de productos por Excel — plantilla multi-hoja COMPLETA y parser.
 * exceljs se carga con import dinámico para no engordar el bundle principal.
 *
 * La plantilla cubre (casi) todos los campos escalares del producto, repartidos
 * en hojas unidas por el `sku`:
 *   Productos · Marketing · Catalogo · Disponibilidad · Cliente · Gastro ·
 *   Running · Cupo · Variantes · Enlaces · Componentes · Instrucciones.
 * Quedan fuera (se hacen en el asistente): imágenes (subida), categorización por
 * nodos de metadatos y colaboradores cross-org.
 *
 * Ver KnowHow/Data/sistemas-importacion-datos-excel.
 */

export type ImportProductType = 'simple' | 'composite' | 'collaborative';

export interface ImportVariant {
  sku: string; name?: string; attributes?: Record<string, string>;
  price?: number; compareAtPrice?: number; stock?: number; stockMinimo?: number;
  barcode?: string; weight?: number; images?: string[]; active?: boolean;
}
export interface ImportProductItem {
  name: string;
  sku?: string;
  type: ImportProductType;
  price?: number;
  variants?: ImportVariant[];
  componentes?: { sku: string; quantity: number; label?: string; isOptional?: boolean; priceOverride?: number }[];
  enlaces?: { tipo: string; url: string; activo?: boolean }[];
  [key: string]: unknown;
}
export interface ParseResult {
  items: ImportProductItem[];
  rowErrors: { row: number; message: string }[];
}

// --- Coerciones + helpers -----------------------------------------------------

const norm = (s: unknown) =>
  String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[\s_]+/g, ' ').trim();
const toStr = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object' && (v as any).text) return String((v as any).text).trim();
  if (typeof v === 'object' && (v as any).result !== undefined) return String((v as any).result).trim();
  return String(v).trim();
};
const toNum = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  if (typeof v === 'number') return v;
  const s = String(v).replace(/[^0-9,.-]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  const n = Number(s); return isNaN(n) ? undefined : n;
};
const toBool = (v: unknown): boolean | undefined => {
  if (typeof v === 'boolean') return v;
  const s = toStr(v).toLowerCase(); if (!s) return undefined;
  return ['si', 'sí', 'true', '1', 'x', 'yes', 'y', 'verdadero'].includes(s);
};
const mapType = (v: string): ImportProductType => {
  const s = norm(v);
  if (['compuesto', 'composite'].includes(s)) return 'composite';
  if (['colaborativo', 'collaborative'].includes(s)) return 'collaborative';
  return 'simple';
};
const mapStatus = (v: string): ImportProductItem['status'] | undefined => {
  const s = norm(v);
  if (['activo', 'active'].includes(s)) return 'active' as any;
  if (['inactivo', 'inactive'].includes(s)) return 'inactive' as any;
  if (['archivado', 'archived'].includes(s)) return 'archived' as any;
  if (['borrador', 'draft'].includes(s)) return 'draft' as any;
  return undefined;
};
const parseKV = (v: string): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const pair of v.split(';')) { const [k, ...rest] = pair.split('='); if (k && rest.length) out[k.trim()] = rest.join('=').trim(); }
  return out;
};
const parseComponents = (v: string) =>
  v.split(';').map((p) => p.trim()).filter(Boolean).map((p) => {
    const [sku, qty] = p.split(':'); return { sku: (sku || '').trim(), quantity: Number(qty) || 1 };
  }).filter((c) => c.sku);

function setPath(obj: any, path: string, value: any) {
  const parts = path.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (cur[p] == null || typeof cur[p] !== 'object') cur[p] = {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
}

// --- Especificación de columnas (dirige generación Y parseo) ------------------

type Kind = 'str' | 'num' | 'bool' | 'csv' | 'kv' | 'boolmap' | 'type' | 'status' | 'date';
interface Col { h: string; f: string; k: Kind; aliases?: string[]; ex?: unknown }

const coerce = (k: Kind, v: unknown): unknown => {
  switch (k) {
    case 'str': { const s = toStr(v); return s || undefined; }
    case 'num': return toNum(v);
    case 'bool': return toBool(v);
    case 'date': { const s = toStr(v); return s || undefined; }
    case 'type': return mapType(toStr(v));
    case 'status': return mapStatus(toStr(v));
    case 'csv': { const s = toStr(v); if (!s) return undefined; const a = s.split(',').map((x) => x.trim()).filter(Boolean); return a.length ? a : undefined; }
    case 'kv': { const s = toStr(v); if (!s) return undefined; const o = parseKV(s); return Object.keys(o).length ? o : undefined; }
    case 'boolmap': { const s = toStr(v); if (!s) return undefined; const o: Record<string, boolean> = {}; s.split(',').map((x) => x.trim()).filter(Boolean).forEach((n) => { o[n] = true; }); return Object.keys(o).length ? o : undefined; }
  }
};

const CORE_COLS: Col[] = [
  { h: 'nombre', f: 'name', k: 'str', aliases: ['name', 'producto', 'titulo'], ex: 'Camiseta VIDA' },
  { h: 'sku', f: 'sku', k: 'str', aliases: ['referencia', 'ref', 'codigo', 'code'], ex: 'CAM-001' },
  { h: 'tipo', f: 'type', k: 'type', aliases: ['type'], ex: 'simple' },
  { h: 'tipo_producto', f: 'tipoProducto', k: 'str', aliases: ['tipoproducto', 'clasificacion'], ex: 'VACA' },
  { h: 'estado', f: 'status', k: 'status', aliases: ['status'], ex: 'borrador' },
  { h: 'descripcion', f: 'description', k: 'str', aliases: ['desc', 'description'], ex: 'Camiseta 100% algodón' },
  { h: 'precio', f: 'price', k: 'num', aliases: ['pvp', 'price'], ex: 15.9 },
  { h: 'descuento', f: 'descuento', k: 'num', aliases: ['discount'], ex: 0 },
  { h: 'iva', f: 'iva', k: 'num', aliases: ['tax'], ex: 21 },
  { h: 'stock', f: 'stock', k: 'num', aliases: ['existencias'], ex: 100 },
  { h: 'stock_minimo', f: 'stockMinimo', k: 'num', aliases: ['stockminimo'], ex: 10 },
  { h: 'alerta_stock', f: 'alertaStock', k: 'bool', ex: 'no' },
  { h: 'ecommerce_activo', f: 'ecommerce_active', k: 'bool', aliases: ['ecommerce'], ex: 'si' },
  { h: 'catalogo_activo', f: 'catalog_active', k: 'bool', aliases: ['catalogo'], ex: 'si' },
  { h: 'tags', f: 'tags', k: 'csv', aliases: ['etiquetas'], ex: 'ropa,algodon' },
  // Catálogo/marketing básicos también inline (para la plantilla SIMPLE).
  { h: 'categoria', f: 'catalog_meta.category', k: 'str', aliases: ['category'], ex: 'Ropa' },
  { h: 'subcategoria', f: 'catalog_meta.subcategory', k: 'str', ex: 'Camisetas' },
  { h: 'marca', f: 'catalog_meta.brand', k: 'str', aliases: ['brand'], ex: 'VIDA' },
  { h: 'descripcion_corta', f: 'catalog_meta.short_description', k: 'str', ex: 'Camiseta básica' },
  { h: 'canal', f: 'marketing.canales.principal', k: 'str', aliases: ['channel'], ex: 'tienda' },
  { h: 'coste_produccion_variable', f: 'parametrosCoste.costeProduccionVariable', k: 'num', aliases: ['c1'], ex: 4 },
  { h: 'coste_producto_fijo', f: 'parametrosCoste.costeProductoFijo', k: 'num', aliases: ['c2'], ex: 2 },
  { h: 'coste_comercial_pet', f: 'parametrosCoste.costeComercialPet', k: 'num', aliases: ['c3'], ex: 5 },
  { h: 'coste_comercial_resto', f: 'parametrosCoste.costeComercialResto', k: 'num', aliases: ['c4'], ex: 1 },
  { h: 'margen', f: 'parametrosCoste.margen', k: 'num', aliases: ['c5', 'margin'], ex: 30 },
  { h: 'descuentos_coste', f: 'parametrosCoste.descuentos', k: 'num', aliases: ['c6'], ex: 0 },
  { h: 'precio_temporada', f: 'parametrosCoste.precioTemporada', k: 'num', aliases: ['c7'], ex: 0 },
  { h: 'min_componentes', f: 'minComponents', k: 'num', ex: '' },
  { h: 'max_componentes', f: 'maxComponents', k: 'num', ex: '' },
  { h: 'componentes', f: '__componentesInline', k: 'str', aliases: ['components'], ex: '' },
];

const DIAS = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

interface AttrSheet { name: string; cols: Col[]; example?: (string | number)[] }

const ATTR_SHEETS: AttrSheet[] = [
  {
    name: 'Marketing',
    cols: [
      { h: 'argumento_venta', f: 'marketing.argumentoVenta', k: 'str' },
      { h: 'descripcion_marketing', f: 'marketing.descripcionMarketing', k: 'str' },
      { h: 'canal_principal', f: 'marketing.canales.principal', k: 'str', aliases: ['canal'] },
      { h: 'canales_adicionales', f: 'marketing.canales.adicionales', k: 'csv' },
      { h: 'og_title', f: 'marketing.metaSeo.ogTitle', k: 'str' },
      { h: 'og_description', f: 'marketing.metaSeo.ogDescription', k: 'str' },
      { h: 'meta_description', f: 'marketing.metaSeo.metaDescription', k: 'str' },
      { h: 'keywords', f: 'marketing.metaSeo.keywords', k: 'str' },
      { h: 'target_audience', f: 'marketing.metaSeo.targetAudience', k: 'str' },
      { h: 'call_to_action', f: 'marketing.metaSeo.callToAction', k: 'str' },
      { h: 'age_range', f: 'marketing.metaSeo.ageRange', k: 'str' },
      { h: 'geo_location', f: 'marketing.metaSeo.geoLocation', k: 'str' },
      { h: 'material_online_activo', f: 'marketing.materialPublicitario.online.activo', k: 'bool' },
      { h: 'material_online_url', f: 'marketing.materialPublicitario.online.url', k: 'str' },
      { h: 'material_digital_activo', f: 'marketing.materialPublicitario.digital.activo', k: 'bool' },
      { h: 'material_digital_archivos', f: 'marketing.materialPublicitario.digital.archivos', k: 'csv' },
      { h: 'material_tradicional_activo', f: 'marketing.materialPublicitario.tradicional.activo', k: 'bool' },
      { h: 'material_tradicional_descripcion', f: 'marketing.materialPublicitario.tradicional.descripcion', k: 'str' },
    ],
  },
  {
    name: 'Catalogo',
    cols: [
      { h: 'descripcion_corta', f: 'catalog_meta.short_description', k: 'str' },
      { h: 'categoria', f: 'catalog_meta.category', k: 'str', aliases: ['category'] },
      { h: 'subcategoria', f: 'catalog_meta.subcategory', k: 'str' },
      { h: 'marca', f: 'catalog_meta.brand', k: 'str', aliases: ['brand'] },
      { h: 'atributos', f: 'catalog_meta.attributes', k: 'kv' },
      { h: 'url_externa', f: 'catalog_meta.external_url', k: 'str' },
      { h: 'cta_texto', f: 'catalog_meta.cta_text', k: 'str' },
      { h: 'destacado', f: 'catalog_meta.featured', k: 'bool' },
      { h: 'seo_meta_title', f: 'catalog_meta.seo.meta_title', k: 'str' },
      { h: 'seo_meta_description', f: 'catalog_meta.seo.meta_description', k: 'str' },
      { h: 'seo_meta_keywords', f: 'catalog_meta.seo.meta_keywords', k: 'csv' },
      { h: 'seo_canonical_url', f: 'catalog_meta.seo.canonical_url', k: 'str' },
      { h: 'landing_url', f: 'catalog_meta.marketing.landing_url', k: 'str' },
      { h: 'highlight_text', f: 'catalog_meta.marketing.highlight_text', k: 'str' },
      { h: 'badge_text', f: 'catalog_meta.marketing.badge_text', k: 'str' },
      { h: 'campaign_tags', f: 'catalog_meta.marketing.campaign_tags', k: 'csv' },
      { h: 'social_share_text', f: 'catalog_meta.marketing.social_share_text', k: 'str' },
    ],
  },
  {
    name: 'Disponibilidad',
    cols: [
      { h: 'tipo_temporada', f: 'aspectosGenerales.tipoTemporada', k: 'str' },
      { h: 'duracion_dias', f: 'aspectosGenerales.duracionDias', k: 'num' },
      { h: 'pernoctas', f: 'aspectosGenerales.pernoctas', k: 'num' },
      { h: 'dia_entrada', f: 'aspectosGenerales.diaEntrada', k: 'bool' },
      { h: 'numero_temporadas', f: 'aspectosGenerales.numeroTemporadas', k: 'num' },
      { h: 'tipo_apertura', f: 'disponibilidadNegocio.tipoApertura', k: 'str' },
      ...DIAS.map((d) => ({ h: `abre_${d}`, f: `disponibilidadNegocio.diasSemana.${d}`, k: 'bool' as Kind })),
      ...MESES.map((m) => ({ h: `abre_${m}`, f: `disponibilidadNegocio.mesesAño.${m}`, k: 'bool' as Kind })),
    ],
  },
  {
    name: 'Cliente',
    cols: [
      { h: 'tipo_cliente', f: 'clienteObjetivo.tipoCliente', k: 'csv' },
      { h: 'cliente_atributos', f: 'clienteObjetivo.atributos', k: 'csv' },
      { h: 'cliente_tamano', f: 'clienteObjetivo.tamaño', k: 'csv' },
      { h: 'perspectiva_costes', f: 'estructuraProducto.perspectivaCostes', k: 'str' },
      { h: 'margen_comercial', f: 'estructuraProducto.margenComercial', k: 'str' },
      { h: 'margen_industrial', f: 'estructuraProducto.margenIndustrial', k: 'str' },
      { h: 'iddi', f: 'estructuraProducto.iddi', k: 'str' },
      { h: 'desc_por_temporada', f: 'descuentosPersonalizados.porTemporada', k: 'num' },
      { h: 'desc_por_volumen', f: 'descuentosPersonalizados.porVolumen', k: 'num' },
      { h: 'desc_pronto_pago', f: 'descuentosPersonalizados.porProntoPago', k: 'num' },
      { h: 'desc_fidelizacion', f: 'descuentosPersonalizados.porFidelizacion', k: 'num' },
      { h: 'desc_oferta', f: 'descuentosPersonalizados.oferta', k: 'num' },
      { h: 'desc_cupon', f: 'descuentosPersonalizados.cupon', k: 'num' },
      { h: 'desc_otro', f: 'descuentosPersonalizados.otroMotivo', k: 'num' },
    ],
  },
  {
    name: 'Gastro',
    cols: [
      { h: 'gastro_activo', f: 'gastro.activo', k: 'bool' },
      { h: 'gastro_tipo_servicio', f: 'gastro.tipoServicio', k: 'str' },
      { h: 'gastro_logistica', f: 'gastro.logistica', k: 'str' },
      { h: 'gastro_relevante_elaborada', f: 'gastro.relevanteElaborada', k: 'bool' },
      { h: 'gastro_descripcion_elaborada', f: 'gastro.descripcionElaborada', k: 'str' },
      { h: 'gastro_alergenos', f: 'gastro.alergenos', k: 'boolmap' },
    ],
  },
  {
    name: 'Running',
    cols: [
      { h: 'running_activo', f: 'running.activo', k: 'bool' },
      { h: 'running_perspectiva_cliente', f: 'running.perspectivaCliente', k: 'str' },
      { h: 'running_horario_inicio', f: 'running.horarioInicio', k: 'str' },
      { h: 'running_horario_fin', f: 'running.horarioFin', k: 'str' },
      { h: 'running_descripcion_actividad', f: 'running.descripcionActividad', k: 'str' },
      { h: 'running_anotacion_singularidades', f: 'running.anotacionSingularidades', k: 'str' },
      { h: 'running_actividades', f: 'running.actividades', k: 'csv' },
      { h: 'running_servicios_terapeuticos', f: 'running.serviciosTerapeuticos', k: 'str' },
      { h: 'running_propuestas_ocio', f: 'running.propuestasOcio', k: 'str' },
      { h: 'running_contenidos_online', f: 'running.contenidosOnline', k: 'str' },
      { h: 'running_otros', f: 'running.otros', k: 'str' },
      { h: 'running_contingencias', f: 'running.contingencias', k: 'str' },
    ],
  },
  {
    name: 'Cupo',
    cols: [
      { h: 'cupo_activo', f: 'cupo.activo', k: 'bool' },
      { h: 'cupo_tipo_limite', f: 'cupo.tipoLimite', k: 'str' },
      { h: 'cupo_cantidad_maxima', f: 'cupo.cantidadMaxima', k: 'num' },
      { h: 'cupo_fecha_limite', f: 'cupo.fechaLimite', k: 'date' },
      { h: 'cupo_descripcion', f: 'cupo.descripcion', k: 'str' },
    ],
  },
  {
    // Categorización por nodos de metadatos (v2). Cada celda: nombre / slug / ruta
    // de nodos existentes, separados por comas. Se resuelven a IDs en el backend.
    name: 'Categorizacion',
    cols: [
      { h: 'vida_interna', f: 'categorization.vida_interna', k: 'csv' },
      { h: 'vida_comercial', f: 'categorization.vida_comercial', k: 'csv' },
      { h: 'pet_interna', f: 'categorization.pet_interna', k: 'csv' },
      { h: 'pet_comercial', f: 'categorization.pet_comercial', k: 'csv' },
      { h: 'colaboradores', f: 'categorization.colaboradores', k: 'csv' },
      { h: 'distintivos_colaborador', f: 'categorization.distintivos_colaborador', k: 'csv' },
      { h: 'distintivos_producto', f: 'categorization.distintivos_producto', k: 'csv' },
      { h: 'cat_marketing', f: 'categorization.marketing', k: 'csv', aliases: ['marketing'] },
    ],
  },
];

/**
 * Hojas de atributos para una organización concreta (VIDA-360): la hoja
 * Categorizacion añade una columna por cada árbol propio de la org (p. ej.
 * `alicun-comercial`), además de las de los árboles globales. La cabecera es el
 * id del árbol.
 */
function attrSheetsFor(extraTreeIds: string[]): AttrSheet[] {
  return ATTR_SHEETS.map((sheet) => {
    if (sheet.name !== 'Categorizacion') return sheet;
    const known = new Set(sheet.cols.map((c) => c.f));
    const extra = extraTreeIds
      .map((id) => ({ h: id, f: `categorization.${id}`, k: 'csv' as Kind }))
      .filter((c) => !known.has(c.f));
    return { ...sheet, cols: [...sheet.cols, ...extra] };
  });
}

const VARIANT_COLS: Col[] = [
  { h: 'sku_variante', f: 'sku', k: 'str', aliases: ['skuvariante', 'sku'] },
  { h: 'nombre', f: 'name', k: 'str' },
  { h: 'atributos', f: 'attributes', k: 'kv' },
  { h: 'precio', f: 'price', k: 'num' },
  { h: 'precio_comparacion', f: 'compareAtPrice', k: 'num' },
  { h: 'stock', f: 'stock', k: 'num' },
  { h: 'stock_minimo', f: 'stockMinimo', k: 'num' },
  { h: 'barcode', f: 'barcode', k: 'str', aliases: ['ean', 'codigo barras'] },
  { h: 'peso', f: 'weight', k: 'num' },
  { h: 'imagenes', f: 'images', k: 'csv' },
  { h: 'activo', f: 'active', k: 'bool' },
];
const ENLACE_COLS: Col[] = [
  { h: 'tipo', f: 'tipo', k: 'str' },
  { h: 'url', f: 'url', k: 'str' },
  { h: 'activo', f: 'activo', k: 'bool' },
];
const COMPONENT_COLS: Col[] = [
  { h: 'sku_componente', f: 'sku', k: 'str' },
  { h: 'cantidad', f: 'quantity', k: 'num' },
  { h: 'etiqueta', f: 'label', k: 'str' },
  { h: 'opcional', f: 'isOptional', k: 'bool' },
  { h: 'precio_override', f: 'priceOverride', k: 'num' },
];

// --- Lectura del workbook -----------------------------------------------------

async function sheetToRows(ws: any): Promise<unknown[][]> {
  const rows: unknown[][] = [];
  ws.eachRow({ includeEmpty: false }, (row: any) => {
    const arr: unknown[] = [];
    row.eachCell({ includeEmpty: true }, (cell: any, colNumber: number) => { arr[colNumber - 1] = cell.value; });
    rows.push(arr);
  });
  return rows;
}

// Localiza la fila de cabecera y devuelve las columnas reconocidas.
function mapCols(rows: unknown[][], cols: Col[]): { headerRow: number; resolved: { col: Col; index: number }[] } {
  const aliasToCol = new Map<string, Col>();
  for (const c of cols) { aliasToCol.set(norm(c.h), c); for (const a of c.aliases || []) aliasToCol.set(norm(a), c); }
  let best = { headerRow: -1, resolved: [] as { col: Col; index: number }[] };
  for (let r = 0; r < Math.min(rows.length, 10); r++) {
    const resolved: { col: Col; index: number }[] = [];
    const seen = new Set<string>();
    (rows[r] || []).forEach((cell, i) => {
      const c = aliasToCol.get(norm(toStr(cell)));
      if (c && !seen.has(c.f)) { seen.add(c.f); resolved.push({ col: c, index: i }); }
    });
    if (resolved.length > best.resolved.length) best = { headerRow: r, resolved };
  }
  return best;
}

const applyCols = (target: any, row: unknown[], resolved: { col: Col; index: number }[]) => {
  for (const { col, index } of resolved) {
    const val = coerce(col.k, row[index]);
    if (val !== undefined) setPath(target, col.f, val);
  }
};

export async function parseProductsWorkbook(file: File, extraTreeIds: string[] = []): Promise<ParseResult> {
  const ExcelJSMod: any = await import('exceljs');
  const ExcelJS = ExcelJSMod.default || ExcelJSMod;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const findSheet = (names: string[]) => wb.worksheets.find((w: any) => names.some((n) => norm(w.name) === norm(n)));

  const rowErrors: { row: number; message: string }[] = [];

  // --- Hoja Productos (crea los items) ---
  const wsProd = findSheet(['Productos', 'Products']) || wb.worksheets[0];
  const prodRows = await sheetToRows(wsProd);
  const { headerRow, resolved } = mapCols(prodRows, CORE_COLS);
  const nameCol = resolved.find((r) => r.col.f === 'name');
  if (headerRow < 0 || !nameCol) {
    rowErrors.push({ row: 0, message: 'No se encontró la columna «nombre» en la hoja Productos.' });
    return { items: [], rowErrors };
  }
  const skuColIdx = resolved.find((r) => r.col.f === 'sku')?.index;

  const items: ImportProductItem[] = [];
  const bySku = new Map<string, ImportProductItem>();
  for (let r = headerRow + 1; r < prodRows.length; r++) {
    const row = prodRows[r];
    const name = toStr(row[nameCol.index]);
    const sku = skuColIdx !== undefined ? toStr(row[skuColIdx]) : '';
    if (!name && !sku) continue;
    if (!name) { rowErrors.push({ row: r + 1, message: 'Falta el nombre.' }); continue; }
    const item: any = { type: 'simple' };
    applyCols(item, row, resolved);
    item.name = name;
    // componentes inline (si el tipo es compuesto y no hay hoja Componentes).
    if (item.__componentesInline) { item.componentes = parseComponents(String(item.__componentesInline)); delete item.__componentesInline; }
    items.push(item);
    if (item.sku) bySku.set(String(item.sku).toUpperCase(), item);
  }

  // --- Hojas de atributos (merge por sku) ---
  for (const sheet of attrSheetsFor(extraTreeIds)) {
    const ws = findSheet([sheet.name]);
    if (!ws) continue;
    const rows = await sheetToRows(ws);
    // La primera columna reconocida como sku es la clave; añadimos un Col sku virtual.
    const cols: Col[] = [{ h: 'sku', f: '__sku', k: 'str', aliases: ['referencia', 'ref', 'codigo'] }, ...sheet.cols];
    const map = mapCols(rows, cols);
    const skuRes = map.resolved.find((r) => r.col.f === '__sku');
    if (map.headerRow < 0 || !skuRes) continue;
    for (let r = map.headerRow + 1; r < rows.length; r++) {
      const row = rows[r];
      const sku = toStr(row[skuRes.index]).toUpperCase();
      if (!sku) continue;
      const item = bySku.get(sku);
      if (!item) { rowErrors.push({ row: r + 1, message: `Hoja ${sheet.name}: SKU «${sku}» no está en la hoja Productos.` }); continue; }
      applyCols(item, row, map.resolved.filter((x) => x.col.f !== '__sku'));
    }
  }

  // --- Hojas hijas (variantes / enlaces / componentes) por sku_padre ---
  const parentKey = (cols: Col[]): Col[] => [{ h: 'sku_padre', f: '__parent', k: 'str', aliases: ['skupadre', 'producto padre', 'parent sku'] }, ...cols];
  const readChild = async (names: string[], cols: Col[], attach: (item: any, obj: any) => void) => {
    const ws = findSheet(names); if (!ws) return;
    const rows = await sheetToRows(ws);
    const map = mapCols(rows, parentKey(cols));
    const pRes = map.resolved.find((x) => x.col.f === '__parent');
    if (map.headerRow < 0 || !pRes) return;
    for (let r = map.headerRow + 1; r < rows.length; r++) {
      const row = rows[r];
      const parent = toStr(row[pRes.index]).toUpperCase();
      if (!parent) continue;
      const item = bySku.get(parent);
      if (!item) { rowErrors.push({ row: r + 1, message: `SKU padre «${parent}» no está en la hoja Productos.` }); continue; }
      const obj: any = {};
      applyCols(obj, row, map.resolved.filter((x) => x.col.f !== '__parent'));
      attach(item, obj);
    }
  };
  await readChild(['Variantes', 'Variants'], VARIANT_COLS, (item, v) => { if (v.sku) { (item.variants ||= []).push(v); } });
  await readChild(['Enlaces', 'Links'], ENLACE_COLS, (item, e) => { if (e.url) { (item.enlaces ||= []).push(e); } });
  await readChild(['Componentes', 'Components'], COMPONENT_COLS, (item, c) => { if (c.sku) { (item.componentes ||= []).push({ sku: c.sku, quantity: c.quantity || 1, label: c.label, isOptional: c.isOptional, priceOverride: c.priceOverride }); } });

  return { items, rowErrors };
}

// --- Generación de la plantilla ----------------------------------------------

const HEX_HEADER = 'FF2F855A';
const styleHeader = (ws: any, count: number) => {
  const row = ws.getRow(1);
  row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEX_HEADER } };
  for (let c = 1; c <= count; c++) ws.getColumn(c).width = 20;
  ws.views = [{ state: 'frozen', ySplit: 1 }];
};
// Construye una fila (array) desde overrides por cabecera; celda vacía si no está.
const rowFrom = (cols: Col[], overrides: Record<string, unknown>) =>
  cols.map((c) => (c.h in overrides ? overrides[c.h] : ''));

const EXAMPLE_FULL: Record<string, unknown> = Object.fromEntries(CORE_COLS.map((c) => [c.h, c.ex ?? '']));
const EXAMPLE_PACK: Record<string, unknown> = {
  nombre: 'Pack 2 camisetas', sku: 'PACK-001', tipo: 'compuesto', tipo_producto: 'GANCHO', estado: 'borrador',
  descripcion: 'Pack de 2 camisetas', precio: 28, descuento: 0, iva: 21, stock: 50, stock_minimo: 5,
  alerta_stock: 'no', ecommerce_activo: 'si', catalogo_activo: 'si', tags: 'pack',
  categoria: 'Ropa', subcategoria: 'Packs', marca: 'VIDA', canal: 'tienda',
  min_componentes: 2, max_componentes: 10, componentes: 'CAM-001:2',
};

const blob = (buf: ArrayBuffer, name: string) => {
  const b = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(b);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

// Columnas de la plantilla SIMPLE (subconjunto del núcleo, los campos más usados).
const SIMPLE_HEADERS = new Set([
  'nombre', 'sku', 'tipo', 'tipo_producto', 'estado', 'descripcion', 'precio', 'descuento', 'iva',
  'stock', 'stock_minimo', 'alerta_stock', 'ecommerce_activo', 'catalogo_activo', 'tags',
  'categoria', 'subcategoria', 'marca', 'descripcion_corta', 'canal',
  'coste_produccion_variable', 'coste_producto_fijo', 'coste_comercial_pet', 'coste_comercial_resto',
  'margen', 'descuentos_coste', 'precio_temporada', 'componentes',
]);
const SIMPLE_COLS = CORE_COLS.filter((c) => SIMPLE_HEADERS.has(c.h));

/** Plantilla SIMPLE — una hoja de productos + variantes + instrucciones. */
export async function downloadProductTemplate(): Promise<void> {
  const ExcelJSMod: any = await import('exceljs');
  const ExcelJS = ExcelJSMod.default || ExcelJSMod;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'VIDA';

  const wsProd = wb.addWorksheet('Productos');
  wsProd.addRow(SIMPLE_COLS.map((c) => c.h));
  styleHeader(wsProd, SIMPLE_COLS.length);
  wsProd.addRow(rowFrom(SIMPLE_COLS, EXAMPLE_FULL));
  wsProd.addRow(rowFrom(SIMPLE_COLS, EXAMPLE_PACK));

  const wsVar = wb.addWorksheet('Variantes');
  wsVar.addRow(['sku_padre', ...VARIANT_COLS.map((c) => c.h)]);
  styleHeader(wsVar, VARIANT_COLS.length + 1);
  wsVar.addRow(['CAM-001', 'CAM-001-S', 'Talla S', 'talla=S;color=azul', 15.9, 0, 40, 5, '', 0.2, '', 'si']);
  wsVar.addRow(['CAM-001', 'CAM-001-M', 'Talla M', 'talla=M;color=azul', 15.9, 0, 60, 5, '', 0.2, '', 'si']);

  const wsHelp = wb.addWorksheet('Instrucciones');
  wsHelp.getColumn(1).width = 26; wsHelp.getColumn(2).width = 100;
  wsHelp.addRow(['Tema', 'Explicación']).font = { bold: true };
  ([
    ['Hoja Productos', '1 fila = 1 producto. «nombre» obligatorio. «sku» recomendado y único (si ya existe en la org, esa fila se OMITE, no se sobreescribe).'],
    ['tipo', 'simple · compuesto · colaborativo. El compuesto se importa DESPUÉS de sus simples.'],
    ['componentes', 'Solo para tipo compuesto: SKU:cantidad;SKU:cantidad (ej. CAM-001:2). Los SKU deben existir o venir en el mismo fichero.'],
    ['categoria/subcategoria/marca', 'Texto libre (catálogo). No hace falta que existan previamente.'],
    ['Hoja Variantes', 'Opcional. 1 fila = 1 variante, ligada por sku_padre. atributos: clave=valor;clave=valor.'],
    ['Booleanos', 'usar si/no (también 1/0, true/false, x).'],
    ['¿Necesitas más campos?', 'Descarga la «plantilla extendida» para marketing, SEO, disponibilidad, gastro/running/cupo, enlaces, etc.'],
    ['Importación', 'Todo pasa por una PREVISUALIZACIÓN con validación; nada se crea hasta que confirmas.'],
  ] as [string, string][]).forEach((h) => wsHelp.addRow(h));

  blob(await wb.xlsx.writeBuffer(), 'plantilla-productos-simple.xlsx');
}

/** Plantilla EXTENDIDA — todas las hojas con (casi) todos los campos. */
export async function downloadProductTemplateExtended(extraTreeIds: string[] = []): Promise<void> {
  const ExcelJSMod: any = await import('exceljs');
  const ExcelJS = ExcelJSMod.default || ExcelJSMod;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'VIDA';

  // Productos
  const wsProd = wb.addWorksheet('Productos');
  wsProd.addRow(CORE_COLS.map((c) => c.h));
  styleHeader(wsProd, CORE_COLS.length);
  wsProd.addRow(rowFrom(CORE_COLS, EXAMPLE_FULL));
  wsProd.addRow(rowFrom(CORE_COLS, EXAMPLE_PACK));

  // Hojas de atributos (cabecera sku + columnas), con una fila de ejemplo para CAM-001.
  for (const sheet of attrSheetsFor(extraTreeIds)) {
    const ws = wb.addWorksheet(sheet.name);
    ws.addRow(['sku', ...sheet.cols.map((c) => c.h)]);
    styleHeader(ws, sheet.cols.length + 1);
    ws.addRow(['CAM-001']); // fila de ejemplo lista para rellenar
  }

  // Variantes
  const wsVar = wb.addWorksheet('Variantes');
  wsVar.addRow(['sku_padre', ...VARIANT_COLS.map((c) => c.h)]);
  styleHeader(wsVar, VARIANT_COLS.length + 1);
  wsVar.addRow(['CAM-001', 'CAM-001-S', 'Talla S', 'talla=S;color=azul', 15.9, 0, 40, 5, '', 0.2, '', 'si']);
  wsVar.addRow(['CAM-001', 'CAM-001-M', 'Talla M', 'talla=M;color=azul', 15.9, 0, 60, 5, '', 0.2, '', 'si']);

  // Enlaces
  const wsEnl = wb.addWorksheet('Enlaces');
  wsEnl.addRow(['sku_padre', ...ENLACE_COLS.map((c) => c.h)]);
  styleHeader(wsEnl, ENLACE_COLS.length + 1);
  wsEnl.addRow(['CAM-001', 'tienda', 'https://tienda.ejemplo/cam-001', 'si']);

  // Componentes
  const wsComp = wb.addWorksheet('Componentes');
  wsComp.addRow(['sku_padre', ...COMPONENT_COLS.map((c) => c.h)]);
  styleHeader(wsComp, COMPONENT_COLS.length + 1);
  wsComp.addRow(['PACK-001', 'CAM-001', 2, 'Camiseta', 'no', -1]);

  // Instrucciones
  const wsHelp = wb.addWorksheet('Instrucciones');
  wsHelp.getColumn(1).width = 26; wsHelp.getColumn(2).width = 100;
  wsHelp.addRow(['Tema', 'Explicación']).font = { bold: true };
  const help: [string, string][] = [
    ['Clave de unión', 'Todas las hojas se unen por el SKU del producto. Rellena «sku» en Productos y usa ese mismo SKU en las demás hojas (sku o sku_padre).'],
    ['Hoja Productos', '1 fila = 1 producto. «nombre» obligatorio. «sku» recomendado y único (si ya existe en la org, esa fila se OMITE).'],
    ['tipo', 'simple · compuesto · colaborativo. El compuesto se importa DESPUÉS de sus simples.'],
    ['componentes (Productos)', 'Atajo para compuestos: SKU:cantidad;SKU:cantidad. Alternativa: usar la hoja Componentes.'],
    ['Hoja Componentes', 'sku_padre = SKU del producto compuesto; sku_componente = SKU del simple; cantidad; precio_override (-1 = usar precio del simple).'],
    ['colaborativo', 'Se marca el tipo, pero los colaboradores de otras organizaciones y sus % se completan en el asistente (requiere activación).'],
    ['Hoja Marketing', 'Argumento, descripción, canales y meta/SEO (Open Graph) + material publicitario. canales_adicionales y listas: separadas por comas.'],
    ['Hoja Catalogo', 'Ficha de catálogo (texto libre): categoría/subcategoría/marca, atributos (clave=valor;clave=valor), SEO y marketing de catálogo.'],
    ['Hoja Disponibilidad', 'Temporada + apertura por día (abre_lunes…abre_domingo) y por mes (abre_enero…abre_diciembre): si/no.'],
    ['Hoja Cliente', 'Tipo de cliente (lista por comas), estructura (márgenes/IDDI) y descuentos personalizados (%).'],
    ['Hojas Gastro / Running / Cupo', 'Solo para productos que apliquen. Marca *_activo = si y rellena el resto. Gastro alérgenos: lista por comas.'],
    ['Hoja Variantes', '1 fila = 1 variante, ligada por sku_padre. atributos: clave=valor;clave=valor. imagenes: URLs por comas.'],
    ['Hoja Enlaces', '1 fila = 1 enlace, ligada por sku_padre. tipo: tienda · catalogo · landing.'],
    ['Hoja Categorizacion', 'Categoriza por NODOS de metadatos existentes. Cada columna es un árbol: los generales (vida_interna, vida_comercial, pet_interna, pet_comercial, colaboradores, distintivos_colaborador, distintivos_producto, cat_marketing) y, al final, los propios de tu organización, con su identificador como cabecera. Valor: nombre / slug / ruta (ej. /vida_comercial/premium) de nodos que YA existan, separados por comas. Los que no se encuentren se avisan (no rompen la fila).'],
    ['Booleanos', 'usar si/no (también valen 1/0, true/false, x).'],
    ['Fuera del Excel', 'Imágenes (se suben en el asistente) y colaboradores cross-org se hacen en el asistente. La categorización por nodos ya se puede en la hoja Categorizacion (contra nodos existentes).'],
    ['Importación', 'Todo pasa por una PREVISUALIZACIÓN con validación; nada se crea hasta que confirmas. Reimportar no duplica (SKU existente = omitido).'],
  ];
  help.forEach((h) => wsHelp.addRow(h));

  blob(await wb.xlsx.writeBuffer(), 'plantilla-productos-extendida.xlsx');
}
